import {
  ClusterStatusChip,
  ClusterStatusTooltip,
  ComputeCostCell,
  ResourceBreakdownCell,
  SavingsCell,
  ScoreBadge,
  ScoreBadgeSkeleton,
  SloCell,
  SloCellSkeleton,
  SloMetricCell,
  SpotUsageCell,
} from '@cast/features-cluster-ui';
import { MouseEvent, ReactNode, useEffect, useRef } from 'react';

import round from 'lodash/round';

import {
  Column,
  EllipsisWithTooltip,
  Icons,
  mergeSx,
  Skeleton,
  TableApi,
  tokens,
  Typography,
  Box,
} from '@cast/design-system';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  NoData,
  NoResults,
  SearchableTable,
  useSearchContext,
} from '@cast/shared-search-ui';
import {
  isClusterManaged,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';
import { Cluster, ClusterDisplayStatus } from '@cast/types';
import { bytesToUnit, convertUnit } from '@cast/utils';

import { generatePath } from 'react-router-dom';

import { useNavigate } from '@cast/infrastructure-organization';

import { ClustersTableSettingsBridge } from './ClustersTableSettingsBridge';
import { buildPresetColumnSettings } from '../../_config/columns';
import type { ClusterViewPreset } from '../../_constants/viewTabs';
import { ClusterNameCell } from '../cells/ClusterNameCell';
import { ManagedByCell } from '../cells/ManagedByCell';
import { ClusterRowActions } from '../ClusterRowActions';
import { clustersTableSx } from '../clustersTableSx';
import { useUtilizationMultiSorters } from './useUtilizationMultiSorters';
import type { GetRowData } from './_types';
import type { RenderClusterRowActions } from '../../types';

const CLUSTERS_TABLE_CACHE_KEY = 'oneclick-clusters-table';

type ClustersTableProps = {
  viewPreset: ClusterViewPreset;
  getRowData: GetRowData;
  emptyStateActions?: ReactNode;
  clusterPathTemplate?: string;
  renderRowActions?: RenderClusterRowActions;
};

const getRegionLabel = (cluster: Cluster) =>
  cluster.region?.displayName || cluster.region?.name || '—';

const TbdCell = ({ label }: { label?: string }) => (
  <Typography
    variant="P12R"
    sx={{ color: tokens.theme.disabled.fg }}
    fontStyle="italic"
  >
    {label ?? '—'}
  </Typography>
);

const StatusCell = ({ on }: { on: boolean }) => (
  <Typography
    variant="P12R"
    sx={{
      color: on
        ? tokens.theme.fg.status.success
        : tokens.theme.fg.structural.secondary,
    }}
  >
    {on ? 'On' : 'Off'}
  </Typography>
);

const skeletonCellSx = {
  borderRadius: '999px',
};

const SummaryLoadingCell = ({
  isLoading,
  children,
}: {
  isLoading?: boolean;
  children: ReactNode;
}) =>
  isLoading ? (
    <Skeleton variant="text" width={72} sx={skeletonCellSx} />
  ) : (
    children
  );

export const ClustersTable = ({
  viewPreset,
  getRowData,
  emptyStateActions,
  clusterPathTemplate = '/clusters/:clusterId',
  renderRowActions,
}: ClustersTableProps) => {
  const navigate = useNavigate();
  const searchContext = useSearchContext<Cluster>();
  const tableApiRef = useRef<TableApi<Cluster> | undefined>(undefined);
  const limitReportingConsole = useShouldLimitConsoleForReporting();
  // SearchableTable only mounts DataTable once data exists; re-apply when it appears.
  const hasTableData = !!searchContext?.filteredData?.length;

  useEffect(() => {
    const { hiddenColumns, columnsOrder } = buildPresetColumnSettings(
      viewPreset,
      { limitReportingConsole }
    );
    tableApiRef.current?.setColumnSettings((settings) => ({
      ...settings,
      hiddenColumns,
      columnsOrder,
    }));
  }, [viewPreset, limitReportingConsole, hasTableData]);

  const handleRowNavigate = (clusterId: string, event: MouseEvent) => {
    navigate(generatePath(clusterPathTemplate, { clusterId }), {
      clickEvent: event,
    });
  };

  const {
    CpuUtilizationMultiSorter,
    MemUtilizationMultiSorter,
    GpuUtilizationMultiSorter,
  } = useUtilizationMultiSorters(getRowData);

  return (
    <SearchableTable
      key={
        limitReportingConsole
          ? 'oneclick-clusters-reporting-limited'
          : 'oneclick-clusters-full'
      }
      apiRef={tableApiRef}
      cacheKey={
        limitReportingConsole
          ? `${CLUSTERS_TABLE_CACHE_KEY}-reporting-limited`
          : CLUSTERS_TABLE_CACHE_KEY
      }
      maxHeight="max(calc(100vh - 480px), 500px)"
      hasFailed={!!searchContext?.error}
      noShadow
      sizing="m"
      rowHeight={40}
      useFixedRowHeight
      sx={mergeSx(clustersTableSx)}
      outerHeader={<ClustersTableSettingsBridge />}
      components={{
        noData: (
          <NoData
            icon={<Icons.Nut />}
            entity="cluster"
            actions={emptyStateActions}
          />
        ),
        noResults: <NoResults icon={<Icons.Nut />} entity="cluster" />,
        failed: (
          <FailedToLoad
            title="Failed to load clusters"
            refresh={searchContext?.refetch}
          />
        ),
      }}
      onRowClick={(event, { row }) => {
        if (!window.getSelection()?.toString() && row.id) {
          handleRowNavigate(row.id, event);
        }
      }}
      getRowTestId={(cluster) => `cluster-row-${cluster.id}`}
      testId="clusters-table"
    >
      <Column<Cluster>
        id="name"
        header="Name"
        frozen
        minWidth={300}
        minResizeWidth={178}
        sortable
        accessor="name"
        resizable
        renderCell={(cluster) => (
          <ClusterNameCell
            cluster={cluster}
            to={generatePath(clusterPathTemplate, {
              clusterId: cluster.id ?? '',
            })}
          />
        )}
      />

      <Column<Cluster>
        id="managedBy"
        header="Managed by"
        minWidth={120}
        sortable
        accessor="providerType"
        renderCell={(cluster) => <ManagedByCell cluster={cluster} />}
      />

      <Column<Cluster>
        id="region"
        header="Region"
        minWidth={110}
        sortable
        accessor={getRegionLabel}
        renderCell={(cluster) => (
          <EllipsisWithTooltip
            TypographyProps={{
              variant: 'P12R',
              sx: { color: tokens.theme.fg.structural.primary },
            }}
          >
            {getRegionLabel(cluster)}
          </EllipsisWithTooltip>
        )}
      />

      <Column<Cluster>
        id="nodes"
        header="Nodes"
        minWidth={90}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSummaryLoading) {
            return <Skeleton variant="text" width={40} sx={skeletonCellSx} />;
          }
          const count = rowData?.summary?.nodesCountTotal?.toNumber();
          if (count === undefined) return <TbdCell />;
          return (
            <Typography
              variant="P12R"
              sx={{ color: tokens.theme.fg.structural.primary }}
            >
              {count}
            </Typography>
          );
        }}
      />

      <Column<Cluster>
        id="cpuEff"
        header="CPU utilization"
        minWidth={160}
        sortable
        Sorter={CpuUtilizationMultiSorter}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          return (
            <SummaryLoadingCell isLoading={rowData?.isSummaryLoading}>
              {(() => {
                const s = rowData?.summary;
                if (!s) return <TbdCell />;
                const total = round(s.cpuAllocatableTotal.toNumber(), 2);
                const used = round(s.cpuUsed.toNumber(), 2);
                const requested = round(s.cpuRequestedTotal.toNumber(), 2);
                return (
                  <ResourceBreakdownCell
                    summaryUnit="CPU"
                    isDiscovered={rowData?.isDiscovered ?? false}
                    testId={`cpu-${cluster.id}`}
                    total={total}
                    used={used}
                    requested={requested}
                  />
                );
              })()}
            </SummaryLoadingCell>
          );
        }}
      />

      <Column<Cluster>
        id="memEff"
        header="Mem utilization"
        minWidth={160}
        sortable
        Sorter={MemUtilizationMultiSorter}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          return (
            <SummaryLoadingCell isLoading={rowData?.isSummaryLoading}>
              {(() => {
                const s = rowData?.summary;
                if (!s) return <TbdCell />;
                const total = convertUnit(
                  s.ramAllocatableTotal.toNumber(),
                  'GiB',
                  'Bytes'
                );
                const used = convertUnit(s.ramUsed.toNumber(), 'GiB', 'Bytes');
                const requested = convertUnit(
                  s.ramRequestedTotal.toNumber(),
                  'GiB',
                  'Bytes'
                );
                const [, ramUnit] = bytesToUnit(total, 2);
                return (
                  <ResourceBreakdownCell
                    summaryUnit={ramUnit}
                    isDiscovered={rowData?.isDiscovered ?? false}
                    testId={`memory-${cluster.id}`}
                    total={total}
                    used={used}
                    requested={requested}
                    convertToUnit={(value) => bytesToUnit(value, 2)}
                  />
                );
              })()}
            </SummaryLoadingCell>
          );
        }}
      />

      <Column<Cluster>
        id="gpuEff"
        header="GPU utilization"
        minWidth={140}
        sortable
        Sorter={GpuUtilizationMultiSorter}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          return (
            <SummaryLoadingCell isLoading={rowData?.isSummaryLoading}>
              {(() => {
                const s = rowData?.summary;
                if (!s) return <TbdCell />;
                const total = s.gpuAllocatableTotal.toNumber();
                const used = s.gpuUsedTotal.toNumber();
                const requested = s.gpuRequestedTotal.toNumber();
                if (!total) return <TbdCell />;
                return (
                  <ResourceBreakdownCell
                    summaryUnit="GPU"
                    isDiscovered={rowData?.isDiscovered ?? false}
                    testId={`gpu-${cluster.id}`}
                    total={total}
                    used={used}
                    requested={requested}
                  />
                );
              })()}
            </SummaryLoadingCell>
          );
        }}
      />

      <Column<Cluster>
        id="spot"
        header="Spot usage"
        minWidth={110}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSummaryLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          const summary = rowData?.summary;
          if (!summary?.nodesCountTotal?.toNumber()) return <TbdCell />;
          return <SpotUsageCell summary={summary} />;
        }}
      />

      <Column<Cluster>
        id="cost"
        header="Cost / mo"
        minWidth={110}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSummaryLoading) {
            return <Skeleton variant="text" width={64} sx={skeletonCellSx} />;
          }
          return (
            <ComputeCostCell monthlyTotal={rowData?.monthlyCost ?? null} />
          );
        }}
      />

      <Column<Cluster>
        id="savings"
        header="Savings / mo"
        minWidth={120}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSavingsLoading) {
            return <Skeleton variant="text" width={72} sx={skeletonCellSx} />;
          }
          return <SavingsCell value={rowData?.monthlySavings ?? null} />;
        }}
      />

      <Column<Cluster>
        id="slo"
        header="SLO"
        minWidth={100}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <SloCellSkeleton />;
          }
          if (!rowData) return <TbdCell />;
          return (
            <SloCell
              breachCount={rowData.slo.breachCount}
              riskCount={rowData.slo.riskCount}
            />
          );
        }}
      />

      <Column<Cluster>
        id="score"
        header="Score"
        minWidth={120}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          const isDiscovered =
            rowData?.isDiscovered ||
            cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;
          if (rowData?.isScoreLoading) {
            return <ScoreBadgeSkeleton />;
          }
          if (isDiscovered) {
            return <ScoreBadge score="?" />;
          }
          if (rowData?.isScoreEvaluating) {
            return <ScoreBadge isEvaluating />;
          }
          if (rowData?.score == null) {
            return <TbdCell />;
          }
          return <ScoreBadge score={rowData.score} />;
        }}
      />

      <Column<Cluster>
        id="autoscaler"
        header="Autoscaler"
        minWidth={120}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          const isDiscovered =
            rowData?.isDiscovered ||
            cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;
          if (rowData?.isAutoscalerStatusLoading) {
            return <Skeleton variant="text" width={28} sx={skeletonCellSx} />;
          }
          if (isDiscovered) {
            return <TbdCell />;
          }
          return (
            <StatusCell
              on={
                !!rowData?.nodeAutoscalerEnabled ||
                !!rowData?.workloadAutoscalerEnabled
              }
            />
          );
        }}
      />

      <Column<Cluster>
        id="wlAutoscaler"
        header="Workload autoscaler"
        minWidth={170}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          const isDiscovered =
            rowData?.isDiscovered ||
            cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;
          if (rowData?.isAutoscalerStatusLoading) {
            return <Skeleton variant="text" width={28} sx={skeletonCellSx} />;
          }
          if (isDiscovered) {
            return <TbdCell />;
          }
          return <StatusCell on={!!rowData?.workloadAutoscalerEnabled} />;
        }}
      />

      <Column<Cluster>
        id="nodeAutoscaler"
        header="Node autoscaler"
        minWidth={160}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          const isDiscovered =
            rowData?.isDiscovered ||
            cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;
          if (rowData?.isAutoscalerStatusLoading) {
            return <Skeleton variant="text" width={28} sx={skeletonCellSx} />;
          }
          if (isDiscovered) {
            return <TbdCell />;
          }
          return <StatusCell on={!!rowData?.nodeAutoscalerEnabled} />;
        }}
      />

      <Column<Cluster>
        id="gpuSharing"
        header="GPU sharing"
        minWidth={120}
        visible={false}
        renderCell={() => <TbdCell />}
      />

      <Column<Cluster>
        id="liveMigration"
        header="Live migration"
        minWidth={130}
        visible={false}
        renderCell={() => <TbdCell />}
      />

      <Column<Cluster>
        id="rebalanced"
        header="Rebalanced"
        minWidth={120}
        visible={false}
        renderCell={() => <TbdCell />}
      />

      <Column<Cluster>
        id="hibernation"
        header="Hibernation"
        minWidth={120}
        visible={false}
        renderCell={() => <TbdCell />}
      />

      <Column<Cluster>
        id="latency"
        header="Latency"
        minWidth={100}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('latency')} />;
        }}
      />

      <Column<Cluster>
        id="errRate"
        header="Error rate"
        minWidth={110}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('errRate')} />;
        }}
      />

      <Column<Cluster>
        id="reqRate"
        header="Request rate"
        minWidth={120}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('reqRate')} />;
        }}
      />

      <Column<Cluster>
        id="oom"
        header="OOM kills"
        minWidth={100}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={40} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('oom')} />;
        }}
      />

      <Column<Cluster>
        id="restarts"
        header="Restarts"
        minWidth={100}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={40} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('restarts')} />;
        }}
      />

      <Column<Cluster>
        id="disrupt"
        header="Disruptions"
        minWidth={110}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={40} sx={skeletonCellSx} />;
          }
          return <SloMetricCell metric={rowData?.getSloMetric('disrupt')} />;
        }}
      />

      <Column<Cluster>
        id="nodeAvailability"
        header="Node availability"
        minWidth={140}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          return (
            <SloMetricCell metric={rowData?.getSloMetric('nodeAvailability')} />
          );
        }}
      />

      <Column<Cluster>
        id="nodeFailures"
        header="Node failures"
        minWidth={120}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={40} sx={skeletonCellSx} />;
          }
          return (
            <SloMetricCell metric={rowData?.getSloMetric('nodeFailures')} />
          );
        }}
      />

      <Column<Cluster>
        id="cpuSaturation"
        header="CPU saturation"
        minWidth={130}
        visible={false}
        renderCell={(cluster) => {
          const rowData = getRowData(cluster.id ?? '');
          if (rowData?.isSloLoading) {
            return <Skeleton variant="text" width={56} sx={skeletonCellSx} />;
          }
          return (
            <SloMetricCell metric={rowData?.getSloMetric('cpuSaturation')} />
          );
        }}
      />

      <Column<Cluster>
        id="status"
        header="Status"
        minWidth={160}
        sortable
        visibilitySettingDisabled
        accessor="displayStatus"
        renderCell={(cluster) => (
          <ClusterStatusTooltip cluster={cluster}>
            <Box display="inline-flex">
              <ClusterStatusChip
                status={cluster.displayStatus}
                managed={
                  isClusterManaged(cluster) || isKentManagedCluster(cluster)
                }
              />
            </Box>
          </ClusterStatusTooltip>
        )}
      />

      <Column<Cluster>
        id="actions"
        header=""
        width={48}
        floating
        visibilitySettingDisabled
        renderCell={(cluster, rowState) => {
          if (renderRowActions) {
            return renderRowActions(cluster, {
              rowIndex: rowState.index,
              onNavigate: handleRowNavigate,
            });
          }

          return (
            <ClusterRowActions
              cluster={cluster}
              onNavigate={(clusterId, event) =>
                handleRowNavigate(clusterId, event)
              }
            />
          );
        }}
      />
    </SearchableTable>
  );
};
